import { getApiBaseUrl } from "./apiEnv";

const API_PREFIX = "/api/v1";

const KNOWN_ERROR_TRANSLATIONS: Array<[string, string]> = [
  ["admin access required", "관리자 권한이 없습니다."],
  ["not authenticated", "인증이 필요합니다. 다시 로그인해 주세요."],
  ["invalid credentials", "아이디 또는 비밀번호가 올바르지 않습니다."],
  ["incorrect username or password", "아이디 또는 비밀번호가 올바르지 않습니다."],
  ["could not validate credentials", "로그인 정보가 유효하지 않습니다."],
  ["forbidden", "접근 권한이 없습니다."],
  ["not found", "요청한 정보를 찾을 수 없습니다."],
  ["already exists", "이미 존재하는 데이터입니다."],
  ["duplicate", "중복된 요청입니다."],
  ["validation error", "입력값 형식이 올바르지 않습니다."],
];

const KOREAN_TEXT_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/;

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  withAuth?: boolean;
};

const isFormDataBody = (body: unknown): body is FormData =>
  typeof FormData !== "undefined" && body instanceof FormData;

export const getStoredToken = (): string | null => localStorage.getItem("admin_access_token");

const toKoreanByStatus = (status: number): string => {
  switch (status) {
    case 400:
      return "요청 형식이 올바르지 않습니다.";
    case 401:
      return "인증이 필요하거나 로그인 정보가 올바르지 않습니다.";
    case 403:
      return "접근 권한이 없습니다.";
    case 404:
      return "요청한 정보를 찾을 수 없습니다.";
    case 409:
      return "이미 존재하는 데이터이거나 충돌이 발생했습니다.";
    case 410:
      return "요청이 만료되었거나 더 이상 유효하지 않습니다.";
    case 413:
      return "파일 용량이 너무 큽니다. 더 작은 파일로 다시 시도해 주세요.";
    case 422:
      return "입력값 형식이 올바르지 않습니다.";
    case 500:
      return "서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
    default:
      return "요청 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";
  }
};

const translateKnownEnglish = (message: string): string | null => {
  const normalized = message.trim().toLowerCase();
  for (const [key, translated] of KNOWN_ERROR_TRANSLATIONS) {
    if (normalized.includes(key)) {
      return translated;
    }
  }
  return null;
};

const extractValidationMessage = (detail: unknown): string | null => {
  if (!Array.isArray(detail) || detail.length === 0) {
    return null;
  }

  const first = detail[0];
  if (typeof first === "string" && first.trim()) {
    const translated = translateKnownEnglish(first);
    return translated ?? "입력값을 확인해 주세요.";
  }

  if (typeof first === "object" && first !== null) {
    const firstDetail = first as Record<string, unknown>;
    const message = typeof firstDetail.msg === "string" ? firstDetail.msg : "";
    if (message) {
      const translated = translateKnownEnglish(message);
      return translated ?? "입력값을 확인해 주세요.";
    }
  }

  return "입력값을 확인해 주세요.";
};

const toKoreanErrorMessage = (path: string, status: number, detail: unknown, statusText: string): string => {
  if (path.includes("/auth/login") && status === 401) {
    return "아이디 또는 비밀번호가 올바르지 않습니다.";
  }

  const validationMessage = extractValidationMessage(detail);
  if (validationMessage) {
    return validationMessage;
  }

  const rawMessage = typeof detail === "string" ? detail : typeof statusText === "string" ? statusText : "";
  if (rawMessage && KOREAN_TEXT_REGEX.test(rawMessage)) {
    return rawMessage;
  }

  if (rawMessage) {
    const translated = translateKnownEnglish(rawMessage);
    if (translated) {
      return translated;
    }
  }

  return toKoreanByStatus(status);
};

const LOGIN_PATH = "/auth/login";

async function consumeFetchResponse(path: string, response: Response): Promise<unknown> {
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    // 공개 페이지는 관리자 로그인으로 보내지 않음
    if (response.status === 401 && !path.includes("/auth/login") && !path.startsWith("/public/")) {
      localStorage.removeItem("admin_access_token");
      alert("로그인이 필요하거나 세션이 만료되었습니다. 로그인 페이지로 이동합니다.");
      window.location.href = "/admin/login";
      throw new ApiError("인증 정보가 유효하지 않아 로그인 페이지로 이동합니다.", 401);
    }

    const objectData = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
    const detail = objectData.detail ?? objectData.message ?? data ?? response.statusText;
    const message = toKoreanErrorMessage(path, response.status, detail, response.statusText);
    throw new ApiError(message, response.status);
  }

  return data;
}

/**
 * FastAPI OAuth2PasswordRequestForm 호환 로그인.
 * POST /api/v1/auth/login — Content-Type: application/x-www-form-urlencoded
 * 필드: username, password (아이디/이메일은 username에 넣음)
 */
export async function authLogin(username: string, password: string): Promise<string> {
  const body = new URLSearchParams();
  body.set("username", username.trim());
  body.set("password", password);

  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${API_PREFIX}${LOGIN_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    });
  } catch {
    throw new ApiError("네트워크 연결을 확인해 주세요.", 0);
  }

  const data = await consumeFetchResponse(LOGIN_PATH, response);
  const obj = typeof data === "object" && data !== null ? (data as Record<string, unknown>) : {};
  const token =
    typeof obj.access_token === "string"
      ? obj.access_token
      : typeof obj.accessToken === "string"
        ? obj.accessToken
        : typeof obj.token === "string"
          ? obj.token
          : null;

  if (!token) {
    throw new ApiError("로그인 응답 형식이 올바르지 않습니다.", response.status);
  }

  return token;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, withAuth = true, ...rest } = options;
  const token = getStoredToken();
  const formData = isFormDataBody(body);

  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${API_PREFIX}${path}`, {
      ...rest,
      headers: {
        ...(formData ? {} : { "Content-Type": "application/json" }),
        ...(withAuth && token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : formData ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("네트워크 연결을 확인해 주세요.", 0);
  }

  const data = await consumeFetchResponse(path, response);
  return data as T;
}
